<template>
  <div class="lobby-container">
    <!-- Global Lobby Menu (persists across all screens) -->
    <div class="lobby-menu-wrapper">
      <LobbyMenu @logout="handleLogout" @how-to-play="handleHowToPlay" @interface-guide="handleInterfaceGuide" @profile="handleProfile" />
    </div>

    <!-- Loading socket connection -->
    <div v-if="isConnectingSocket" class="mode-selection">
      <div class="welcome-section">
        <h1 class="welcome-title">Connecting to game server...</h1>
        <p class="subtitle">Please wait</p>
      </div>
    </div>

    <!-- Socket connection error -->
    <div v-else-if="socketError" class="mode-selection">
      <div class="welcome-section">
        <h1 class="welcome-title">Connection Failed</h1>
        <p class="subtitle error-text">{{ socketError }}</p>
        <button class="u-btn-primary" @click="reloadPage">
          Refresh Page
        </button>
      </div>
    </div>

    <!-- Mode Selection (initial screen) -->
    <div v-else-if="!mode && !showWaitingRoom" class="mode-selection">
      <!-- Logo in its own column on left -->
      <div class="logo-column">
        <img
          src="/images/logo.png"
          alt="Canasta - Hooley Style"
          class="lobby-logo"
        />
      </div>

      <!-- Main content area -->
      <div class="main-content">
        <!-- Header with welcome + history button -->
        <div class="content-header">
          <div class="welcome-text">
            <h1 class="welcome-title">Welcome, {{ username }}!</h1>
            <p class="subtitle">Ready to play Canasta?</p>
          </div>
          <button
            class="u-glass-btn"
            :class="{ active: showHistory }"
            @click="toggleHistory"
          >
            <span class="material-symbols-outlined">history</span>
            History
          </button>
        </div>

        <!-- Vertical layout: actions row + recent games below -->
        <div class="content-body">
          <!-- Actions row (Create/Join side by side) -->
          <div class="actions-row">
            <button class="mode-card" data-tutorial="create-game" @click="setMode('create')">
              <span class="material-symbols-outlined mode-icon">add_circle</span>
              <h3>Create Game</h3>
              <p>Start a new game or play with bots</p>
            </button>

            <button class="mode-card" data-tutorial="join-game" @click="setMode('join')">
              <span class="material-symbols-outlined mode-icon">group_add</span>
              <h3>Join Game</h3>
              <p>Enter room code to join friends</p>
            </button>
          </div>

          <!-- Recent games section below actions -->
          <div class="games-section">
            <div v-if="!showHistory" class="recent-games-section" data-tutorial="recent-games">
              <h3 class="section-title">
                <span class="material-symbols-outlined">schedule</span>
                Recent Games
                <span v-if="recentGamesCount > 0" class="game-count">({{ recentGamesCount }})</span>
              </h3>
              <RecentGamesList
                :games="recentGames"
                :loading="isLoadingRecent"
                :disabled="isJoining"
                @resume="handleResumeGame"
                @delete="handleDeleteGame"
                @delete-all="handleDeleteAllGames"
              />
            </div>

            <div v-else class="history-section">
              <h3 class="section-title">
                <span class="material-symbols-outlined">history</span>
                Game History
              </h3>
              <GameHistoryList
                :games="historicalGames"
                :loading="isLoadingHistory"
                :has-more="hasMoreHistory"
                @view="handleViewGame"
                @load-more="loadMoreHistory"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Game Detail Modal -->
      <GameDetailModal
        v-if="selectedGame"
        :game="selectedGame"
        :loading="isLoadingDetail"
        @close="closeGameDetail"
      />

      <!-- How to Play Modal -->
      <HowToPlayModal
        :is-open="showHowToPlay"
        @close="showHowToPlay = false"
      />
    </div>

    <!-- Create Game Screen -->
    <div v-else-if="mode === 'create' && !showWaitingRoom" class="create-game-screen">
      <div class="glass-container">
        <h1 class="screen-title">Create New Game</h1>

        <!-- Display Name -->
        <div class="form-section">
          <label for="display-name" class="section-label">Display Name</label>
          <input
            id="display-name"
            v-model="displayName"
            type="text"
            class="text-input"
            placeholder="Enter your name"
            maxlength="20"
          />
        </div>

        <!-- Avatar Selection Carousel -->
        <div class="form-section">
          <label class="section-label">Avatar</label>
          <div class="avatar-carousel" :class="{ spinning: isSpinning }" data-tutorial="avatar-carousel">
            <button type="button" class="carousel-arrow left" @click="prevAvatar" :disabled="isSpinning">
              <span class="material-symbols-outlined">chevron_left</span>
            </button>

            <div class="carousel-track">
              <!-- Previous avatar (behind, left) -->
              <div class="carousel-item prev">
                <img :src="`/images/profiles/${prevAvatarName}.png`" :alt="prevAvatarName" />
              </div>
              <!-- Current avatar (front, center) -->
              <div class="carousel-item current">
                <img :src="`/images/profiles/${profilePicture}.png`" :alt="profilePicture" />
              </div>
              <!-- Next avatar (behind, right) -->
              <div class="carousel-item next">
                <img :src="`/images/profiles/${nextAvatarName}.png`" :alt="nextAvatarName" />
              </div>
            </div>

            <button type="button" class="carousel-arrow right" @click="nextAvatar" :disabled="isSpinning">
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <button type="button" class="random-avatar-btn" @click="randomAvatar" :disabled="isSpinning" title="Random avatar">
            🎲
          </button>
        </div>

        <!-- Team Name -->
        <div class="form-section">
          <label class="section-label">Give your team a name</label>
          <div class="team-name-input" data-tutorial="team-name">
            <input
              v-model="teamConfig.name"
              type="text"
              class="text-input team-name-field"
              :class="{ 'generated-name': isTeamNameGenerated }"
              maxlength="55"
              @input="isTeamNameGenerated = false"
              @focus="handleTeamNameFocus"
            />
            <button type="button" class="randomize-btn" @click="regenerateTeamName" title="Generate random name">
              🎲
            </button>
          </div>
        </div>

        <!-- Game Mode Cards -->
        <div class="game-mode-cards" data-tutorial="game-mode">
          <label class="mode-card" :class="{ active: gameMode === 'bots' }">
            <input v-model="gameMode" type="radio" name="game-mode" value="bots" class="mode-input" />
            <div class="mode-indicator"></div>
            <div class="mode-content">
              <span class="mode-title">Play with Bots</span>
              <span class="mode-desc">Start immediately</span>
            </div>
          </label>
          <label class="mode-card" :class="{ active: gameMode === 'humans' }">
            <input v-model="gameMode" type="radio" name="game-mode" value="humans" class="mode-input" />
            <div class="mode-indicator"></div>
            <div class="mode-content">
              <span class="mode-title">Wait for Players</span>
              <span class="mode-desc">Invite friends</span>
            </div>
          </label>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button
            class="btn-create"
            data-tutorial="start-game-btn"
            :disabled="isCreating || !displayName.trim() || !socket.isConnected()"
            @click="handleCreateGame"
          >
            {{ isCreating ? 'Creating...' : 'Create Game' }}
          </button>
          <button class="btn-cancel" @click="cancelCreateGame">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Join Game Screen -->
    <div v-else-if="mode === 'join' && !showWaitingRoom" class="join-game-screen">
      <div class="glass-container">
        <h1 class="screen-title">Join Game</h1>

        <!-- Room Code -->
        <div class="form-section">
          <label for="room-code" class="section-label">Room Code</label>
          <input
            id="room-code"
            v-model="roomCode"
            type="text"
            class="text-input room-code-input"
            placeholder="Enter 6-character code"
            maxlength="6"
            @keyup.enter="handleJoinGame"
          />
        </div>

        <!-- Display Name -->
        <div class="form-section">
          <label for="join-display-name" class="section-label">Display Name</label>
          <input
            id="join-display-name"
            v-model="displayName"
            type="text"
            class="text-input"
            placeholder="Enter your display name"
            maxlength="20"
          />
        </div>

        <!-- Avatar Selection Carousel (same as Create Game) -->
        <div class="form-section">
          <label class="section-label">Avatar</label>
          <div class="avatar-carousel" :class="{ spinning: isSpinning }">
            <button type="button" class="carousel-arrow left" @click="prevAvatar" :disabled="isSpinning">
              <span class="material-symbols-outlined">chevron_left</span>
            </button>

            <div class="carousel-track">
              <!-- Previous avatar (behind, left) -->
              <div class="carousel-item prev">
                <img :src="`/images/profiles/${prevAvatarName}.png`" :alt="prevAvatarName" />
              </div>
              <!-- Current avatar (front, center) -->
              <div class="carousel-item current">
                <img :src="`/images/profiles/${profilePicture}.png`" :alt="profilePicture" />
              </div>
              <!-- Next avatar (behind, right) -->
              <div class="carousel-item next">
                <img :src="`/images/profiles/${nextAvatarName}.png`" :alt="nextAvatarName" />
              </div>
            </div>

            <button type="button" class="carousel-arrow right" @click="nextAvatar" :disabled="isSpinning">
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <button type="button" class="random-avatar-btn" @click="randomAvatar" :disabled="isSpinning" title="Random avatar">
            🎲
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button
            class="btn-create"
            :disabled="!roomCode.trim() || !displayName.trim() || isJoining || !socket.isConnected()"
            @click="handleJoinGame"
          >
            {{ isJoining ? 'Joining...' : 'Join Game' }}
          </button>
          <button class="btn-cancel" @click="cancelJoinGame">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Waiting Room (for friends mode) -->
    <WaitingRoom
      v-else-if="showWaitingRoom"
      :room-code="waitingRoomData.roomCode"
      :players="waitingRoomData.players"
      :teams="waitingRoomData.teams"
      :current-user-id="currentPlayerId"
      :creator-user-id="waitingRoomData.creatorUserId"
      :status="waitingRoomData.status"
      @start-game="handleWaitingRoomStart"
      @add-bot="handleWaitingRoomAddBot"
      @kick-player="handleWaitingRoomKick"
      @leave-game="handleWaitingRoomLeave"
      @back="handleWaitingRoomBack"
      @logout="handleLogout"
    />

    <!-- Paused Game Lobby (for resuming paused games) -->
    <PausedGameLobby
      v-else-if="showPausedGameLobby"
      :room-code="pausedGameData.roomCode"
      :game-id="pausedGameData.gameId"
      :players="pausedGameData.players"
      :round-number="pausedGameData.roundNumber"
      :team-a-name="pausedGameData.teamAName"
      :team-b-name="pausedGameData.teamBName"
      :team-a-score="pausedGameData.teamAScore"
      :team-b-score="pausedGameData.teamBScore"
      :creator-user-id="pausedGameData.creatorUserId"
      :current-user-id="props.userId || ''"
      @back="handlePausedLobbyBack"
      @resume-game="handlePausedLobbyResume"
      @replace-with-bot="handlePausedLobbyReplaceBot"
      @send-invite="handlePausedLobbySendInvite"
    />

    <!-- Join Status Modal (shows progress during join flow) -->
    <JoinStatusModal
      :is-open="showJoinStatus"
      :current-step="joinStep"
      :room-code="roomCode.toUpperCase()"
      :status-message="joinStatusMessage"
      :has-error="joinError"
      @cancel="handleJoinCancel"
    />

    <!-- Stale Game Recovery Modal -->
    <StaleGameRecoveryModal
      :is-open="showRecoveryModal"
      :data="recoveryData"
      @action="handleRecoveryAction"
    />

    <!-- Interface Guide Tutorials -->
    <InterfaceGuide
      ref="interfaceGuideRef"
      context="lobby"
    />
    <InterfaceGuide
      context="create-game"
    />

    <!-- First-Visit Welcome Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showFirstVisitPrompt" class="first-visit-overlay">
          <div class="first-visit-modal">
            <div class="first-visit-header">
              <span class="material-symbols-outlined welcome-icon">waving_hand</span>
              <h2>Welcome to Canasta!</h2>
            </div>
            <div class="first-visit-body">
              <p>Would you like a quick tour of the interface?</p>
              <p class="hint-text">You can always access the Interface Guide from the menu later.</p>
            </div>
            <div class="first-visit-footer">
              <button class="u-glass-btn" @click="handleSkipFirstVisit">
                Skip for Now
              </button>
              <button class="u-btn-primary u-flex u-items-center u-gap-2" @click="handleStartTutorial">
                <span class="material-symbols-outlined">school</span>
                Start Tour
              </button>
            </div>
            <label class="never-show-checkbox">
              <input type="checkbox" v-model="neverShowAgain" />
              <span>Don't show this again</span>
            </label>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useSocket } from '@/composables/useSocket'
import { useGameHistory } from '@/composables/useGameHistory'
import { Logger, LogCategory } from '@/utils/Logger.class'
import RecentGamesList from './RecentGamesList.vue'
import GameHistoryList from './GameHistoryList.vue'
import GameDetailModal from './GameDetailModal.vue'
import WaitingRoom from './WaitingRoom.vue'
import PausedGameLobby from './PausedGameLobby.vue'
import LobbyMenu from '@/components/common/LobbyMenu.vue'
import HowToPlayModal from '@/components/modals/HowToPlayModal.vue'
import InterfaceGuide from '@/components/tutorial/InterfaceGuide.vue'
import { useTutorial } from '@/composables/useTutorial'
import JoinStatusModal from '@/components/modals/JoinStatusModal.vue'
import StaleGameRecoveryModal from '@/components/modals/StaleGameRecoveryModal.vue'
import type { GameSummary } from '@/types/game'
import { PROFILE_PICTURES } from '@/config/avatars'
// TeamCustomization imported for future use
import _TeamCustomization from './TeamCustomization.vue'
void _TeamCustomization

// API URL configuration (kept for future use)
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const _API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`
void _API_URL // Suppress unused variable warning

/**
 * GameLobby Component
 *
 * Main lobby screen with three states:
 * - Mode selection: Choose between Create or Join
 * - Create Game: Set up a new game with custom team and settings
 * - Join Game: Enter room code to join existing game
 *
 * Features:
 * - Profile picture selection (16 avatars)
 * - Display name customization
 * - Team customization (name + icon) for creating games
 * - Bot or human game mode selection
 */

// ============================================================================
// Props & Emits
// ============================================================================

interface Props {
  username: string
  userId: string | null
}

interface Emits {
  (e: 'game-created', data: any): void
  (e: 'game-joined', data: any): void
  (e: 'game-start'): void
  (e: 'error', message: string): void
  (e: 'logout'): void
  (e: 'profile'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// ============================================================================
// Constants
// ============================================================================

// PROFILE_PICTURES imported from @/config/avatars

// Team name generator word lists
const TEAM_NAME_HINT = ' (Type in your own name)'
const TEAM_NAME_PREFIXES = [
  'Shadow', 'Thunder', 'Golden', 'Iron', 'Crimson', 'Storm',
  'Phantom', 'Royal', 'Savage', 'Mystic', 'Elite', 'Blazing',
  'Midnight', 'Silver', 'Raging', 'Noble', 'Dark', 'Fierce',
  'Ancient', 'Eternal', 'Frozen', 'Burning', 'Silent', 'Wild'
]
const TEAM_NAME_SUFFIXES = [
  'Dragons', 'Wolves', 'Knights', 'Legends', 'Titans', 'Phoenix',
  'Raiders', 'Aces', 'Guardians', 'Vipers', 'Hawks', 'Warriors',
  'Serpents', 'Lions', 'Ravens', 'Bears', 'Falcons', 'Panthers',
  'Sharks', 'Stallions', 'Cobras', 'Eagles', 'Foxes', 'Jaguars'
]

// ============================================================================
// Composables
// ============================================================================

const socket = useSocket()
const gameHistory = useGameHistory()

// ============================================================================
// State
// ============================================================================

const mode = ref<'create' | 'join' | null>(null)
const displayName = ref('')
const profilePicture = ref<string>(PROFILE_PICTURES[0] ?? 'alien')
const avatarIndex = ref(0)
const isSpinning = ref(false)
const teamConfig = ref({
  name: '',
})
const isTeamNameGenerated = ref(true)

// Computed properties for carousel navigation
const prevAvatarName = computed(() =>
  PROFILE_PICTURES[(avatarIndex.value - 1 + PROFILE_PICTURES.length) % PROFILE_PICTURES.length]
)
const nextAvatarName = computed(() =>
  PROFILE_PICTURES[(avatarIndex.value + 1) % PROFILE_PICTURES.length]
)
const gameMode = ref<'bots' | 'humans'>('bots')
const roomCode = ref('')
const isCreating = ref(false)
const isJoining = ref(false)

// Join status modal state
const showJoinStatus = ref(false)
const joinStep = ref(0) // 0 = Sending, 1 = Waiting approval, 2 = Joining
const joinStatusMessage = ref('')
const joinError = ref(false)

// Stale game recovery modal state
const showRecoveryModal = ref(false)
const recoveryData = ref<{
  room_code: string
  game_state: any
  your_player_id: string
  is_creator: boolean
  absent_players: Array<{
    id: string
    username: string
    display_name?: string
    seat: number
    team: string
  }>
  connected_players?: string[]
  message?: string
} | null>(null)

// Waiting room state (for friends/humans mode)
const showWaitingRoom = ref(false)
const currentPlayerId = ref('') // Track current player's game-specific ID
const waitingRoomData = ref<{
  roomCode: string
  players: Array<{
    id: string
    username: string
    display_name?: string
    profile_picture?: string
    seat: number
    team: string
    is_bot: boolean
  }>
  teams: { A: { name: string }; B: { name: string } }
  creatorUserId: string
  status: string
}>({
  roomCode: '',
  players: [],
  teams: { A: { name: 'Team A' }, B: { name: 'Team B' } },
  creatorUserId: '',
  status: 'waiting',
})

// Paused game lobby state
const showPausedGameLobby = ref(false)
const pausedGameData = ref<{
  roomCode: string
  gameId: string
  players: Array<{
    seat_position: number
    team: string
    username: string
    display_name: string
    profile_picture: string
    is_bot: boolean
    current_id: string
    original_user_id?: string | null
    replaced_by_bot?: boolean
    status: 'rejoined' | 'waiting' | 'bot'
  }>
  roundNumber: number
  teamAName: string
  teamBName: string
  teamAScore: number
  teamBScore: number
  creatorUserId: string
}>({
  roomCode: '',
  gameId: '',
  players: [],
  roundNumber: 1,
  teamAName: 'Team A',
  teamBName: 'Team B',
  teamAScore: 0,
  teamBScore: 0,
  creatorUserId: '',
})

// Destructure gameHistory for template access
const {
  activeTab,
  recentGames,
  historicalGames,
  selectedGame,
  isLoadingRecent,
  isLoadingHistory,
  isLoadingDetail,
  hasMoreHistory,
  recentGamesCount,
  setTab,
  fetchRecentGames,
  fetchHistoricalGames: _fetchHistoricalGames,
  fetchGameDetail,
  loadMoreHistory,
  closeGameDetail,
} = gameHistory
void _fetchHistoricalGames // Suppress unused variable warning

// Socket connection state
const isConnectingSocket = ref(false)
const socketError = ref<string | null>(null)

// History toggle state
const showHistory = ref(false)

// How to Play modal state
const showHowToPlay = ref(false)

// Tutorial state
const tutorial = useTutorial()
const interfaceGuideRef = ref<InstanceType<typeof InterfaceGuide> | null>(null)
const showFirstVisitPrompt = ref(false)
const neverShowAgain = ref(false)

// ============================================================================
// Lifecycle Hooks
// ============================================================================

onMounted(async () => {
  // Load saved preferences from localStorage
  const savedDisplayName = localStorage.getItem('canasta_display_name')
  const savedProfile = localStorage.getItem('canasta_profile_picture')

  if (savedDisplayName) {
    displayName.value = savedDisplayName
  } else {
    displayName.value = props.username
  }

  if (savedProfile && PROFILE_PICTURES.includes(savedProfile)) {
    profilePicture.value = savedProfile
    avatarIndex.value = PROFILE_PICTURES.indexOf(savedProfile)
  }

  // Generate a random team name
  teamConfig.value.name = generateTeamName()

  // Initialize socket connection
  console.log('[GameLobby] Mounted - Initializing socket connection')
  isConnectingSocket.value = true
  socketError.value = null

  try {
    await socket.connect()
    console.log('[GameLobby] Socket connected successfully')
    console.log('[GameLobby] Socket ID:', socket.getSocket()?.id)
    console.log('[GameLobby] Socket connected:', socket.isConnected())

    // Set up socket listeners for waiting room updates
    socket.on('player_joined', (data: any) => {
      if (showWaitingRoom.value && data.player) {
        console.log('[GameLobby] Player joined waiting room:', data.player)
        const existingIndex = waitingRoomData.value.players.findIndex(p => p.id === data.player.id)
        if (existingIndex === -1) {
          waitingRoomData.value.players.push({
            id: data.player.id,
            username: data.player.username,
            display_name: data.player.display_name,
            profile_picture: data.player.profile_picture,
            seat: data.player.seat,
            team: data.player.team,
            is_bot: data.player.is_bot || false,
          })
        }
      }
    })

    socket.on('bot_added', (data: any) => {
      if (showWaitingRoom.value && data.bot) {
        console.log('[GameLobby] Bot added to waiting room:', data.bot)
        const existingIndex = waitingRoomData.value.players.findIndex(p => p.seat === data.bot.seat)
        if (existingIndex === -1) {
          waitingRoomData.value.players.push({
            id: data.bot.id || `bot-${data.bot.seat}`,
            username: data.bot.username || `Bot ${data.bot.seat + 1}`,
            display_name: data.bot.display_name,
            profile_picture: data.bot.profile_picture,
            seat: data.bot.seat,
            team: data.bot.team,
            is_bot: true,
          })
        }
      }
    })

    socket.on('player_left', (data: any) => {
      if (showWaitingRoom.value && data.player_id) {
        console.log('[GameLobby] Player left waiting room:', data.player_id)
        waitingRoomData.value.players = waitingRoomData.value.players.filter(
          p => p.id !== data.player_id
        )
      }
    })

    socket.on('game_started', (data: any) => {
      // Handle game started from waiting room
      if (showWaitingRoom.value) {
        console.log('[GameLobby] Game started from waiting room')
        showWaitingRoom.value = false
        emit('game-start')
        return
      }

      // Handle game resumed from paused lobby
      if (showPausedGameLobby.value) {
        console.log('[GameLobby] Game resumed from paused lobby', data?.resumed ? '(resumed)' : '')
        showPausedGameLobby.value = false
        emit('game-joined', { roomCode: pausedGameData.value.roomCode })
        emit('game-start')
        return
      }
    })

    // Stale game recovery events
    socket.on('stale_game_recovery', (data: any) => {
      console.log('[GameLobby] Stale game recovery received:', data)
      // Hide any join status modal
      showJoinStatus.value = false
      isJoining.value = false
      // Store recovery data and show modal
      recoveryData.value = data
      showRecoveryModal.value = true
    })

    socket.on('recovery_complete', (data: any) => {
      console.log('[GameLobby] Recovery complete:', data)
      showRecoveryModal.value = false

      if (data.action === 'wait') {
        // Waiting for players - emit game-joined and start
        if (recoveryData.value) {
          emit('game-joined', { roomCode: recoveryData.value.room_code })
          emit('game-start')
        }
      } else if (data.action === 'replace_with_bots') {
        // Bots replaced - start game with updated state
        if (recoveryData.value) {
          emit('game-joined', { roomCode: recoveryData.value.room_code })
          emit('game-start')
        }
      }
    })

    // Paused game lobby events
    socket.on('paused_game_lobby', (data: any) => {
      console.log('[GameLobby] Paused game lobby received:', data)
      isJoining.value = false

      // Populate paused game data
      pausedGameData.value = {
        roomCode: data.room_code,
        gameId: data.game_id,
        players: data.players || [],
        roundNumber: data.round_number || 1,
        teamAName: data.team_a_name || 'Team A',
        teamBName: data.team_b_name || 'Team B',
        teamAScore: data.team_a_score || 0,
        teamBScore: data.team_b_score || 0,
        creatorUserId: data.creator_user_id || '',
      }

      // Show the paused game lobby
      showPausedGameLobby.value = true
    })

    socket.on('paused_lobby_updated', (data: any) => {
      console.log('[GameLobby] Paused lobby updated:', data)
      if (showPausedGameLobby.value && data.players) {
        pausedGameData.value.players = data.players
      }
    })

    // Fetch recent games for the game retrieval feature
    fetchRecentGames().catch(err => {
      console.warn('[GameLobby] Could not fetch recent games:', err)
    })
  } catch (error: any) {
    console.error('[GameLobby] Failed to connect socket:', error)
    socketError.value = error.message || 'Failed to connect to game server'
  } finally {
    isConnectingSocket.value = false
    console.log('[GameLobby] Socket initialization complete, connected:', socket.isConnected())

    // Check for first-time user and show tutorial prompt
    checkFirstTimeUser()
  }
})

// ============================================================================
// Methods
// ============================================================================

/**
 * Set lobby mode (create, join, or null to return to selection)
 */
function setMode(newMode: 'create' | 'join' | null) {
  mode.value = newMode

  // Continue first-time flow if entering create-game screen
  if (newMode === 'create') {
    tutorial.continueFirstTimeFlow('create-game')
  }
}

/**
 * Toggle between Recent Games and History view
 */
function toggleHistory() {
  showHistory.value = !showHistory.value
  // Fetch history data if switching to history view
  if (showHistory.value && historicalGames.value.length === 0) {
    setTab('history')
  }
}

/**
 * Navigate to next avatar in carousel
 */
function nextAvatar() {
  avatarIndex.value = (avatarIndex.value + 1) % PROFILE_PICTURES.length
  profilePicture.value = PROFILE_PICTURES[avatarIndex.value]
  localStorage.setItem('canasta_profile_picture', profilePicture.value)
}

/**
 * Navigate to previous avatar in carousel
 */
function prevAvatar() {
  avatarIndex.value = (avatarIndex.value - 1 + PROFILE_PICTURES.length) % PROFILE_PICTURES.length
  profilePicture.value = PROFILE_PICTURES[avatarIndex.value]
  localStorage.setItem('canasta_profile_picture', profilePicture.value)
}

/**
 * Spin to a random avatar with animation
 */
function randomAvatar() {
  if (isSpinning.value) return
  isSpinning.value = true

  const targetIndex = Math.floor(Math.random() * PROFILE_PICTURES.length)
  const spins = 2 + Math.floor(Math.random() * 2) // 2-3 full rotations
  const totalSteps = spins * PROFILE_PICTURES.length + targetIndex
  const currentIdx = avatarIndex.value
  const stepsToTarget = (targetIndex - currentIdx + PROFILE_PICTURES.length) % PROFILE_PICTURES.length

  let step = 0
  const baseDelay = 50
  const maxDelay = 200

  function animateStep() {
    if (step >= totalSteps) {
      isSpinning.value = false
      return
    }

    nextAvatar()
    step++

    // Ease out - slow down as we approach the end
    const progress = step / totalSteps
    const delay = baseDelay + (maxDelay - baseDelay) * Math.pow(progress, 2)

    setTimeout(animateStep, delay)
  }

  animateStep()
}

/**
 * Generate a random epic team name
 */
function generateTeamName(): string {
  const prefix = TEAM_NAME_PREFIXES[Math.floor(Math.random() * TEAM_NAME_PREFIXES.length)]
  const suffix = TEAM_NAME_SUFFIXES[Math.floor(Math.random() * TEAM_NAME_SUFFIXES.length)]
  return `${prefix} ${suffix}${TEAM_NAME_HINT}`
}

function getCleanTeamName(): string {
  return teamConfig.value.name.replace(TEAM_NAME_HINT, '').trim()
}

/**
 * Regenerate team name with a new random one
 */
function regenerateTeamName() {
  teamConfig.value.name = generateTeamName()
  isTeamNameGenerated.value = true
}

/**
 * Handle team name input focus - clear if it's a generated name
 */
function handleTeamNameFocus() {
  if (isTeamNameGenerated.value) {
    teamConfig.value.name = ''
    isTeamNameGenerated.value = false
  }
}

/**
 * Reload the page
 */
function reloadPage() {
  window.location.reload()
}

/**
 * Cancel create game and return to mode selection
 */
function cancelCreateGame() {
  setMode(null)
}

/**
 * Cancel join game and return to mode selection
 */
function cancelJoinGame() {
  setMode(null)
  roomCode.value = ''
}

/**
 * Handle create game button click
 */
async function handleCreateGame() {
  console.log('[GameLobby] handleCreateGame called')
  console.log('[GameLobby] isCreating:', isCreating.value, 'displayName:', displayName.value)

  if (isCreating.value || !displayName.value.trim()) return

  // Check socket connection
  console.log('[GameLobby] Socket connected:', socket.isConnected())
  console.log('[GameLobby] Socket ID:', socket.getSocket()?.id)

  if (!socket.isConnected()) {
    console.error('[GameLobby] Socket not connected, aborting create game')
    emit('error', 'Not connected to game server. Please refresh the page.')
    return
  }

  // Save display name to localStorage
  localStorage.setItem('canasta_display_name', displayName.value.trim())

  isCreating.value = true
  console.log('[GameLobby] Creating room with config:', {
    username: props.username,
    userId: props.userId,
    teamName: teamConfig.value.name,
    displayName: displayName.value.trim(),
    profilePicture: profilePicture.value,
  })

  try {
    // Create room via socket
    const response = await socket.createRoom({
      username: props.username,
      userId: props.userId,
      teamName: getCleanTeamName(),
      displayName: displayName.value.trim(),
      profilePicture: profilePicture.value,
      gameMode: gameMode.value, // Send bot/human mode to backend
    })

    console.log('[GameLobby] Room created successfully:', response)
    console.log('[GameLobby] Socket ID after createRoom:', socket.getSocket()?.id)

    // Persist player ID and creator flag for GameBoard
    if (response.player_id) {
      localStorage.setItem('canasta_player_id', response.player_id)
      console.log('[GameLobby] Persisted player_id to localStorage:', response.player_id)
    }
    localStorage.setItem('canasta_is_creator', 'true')
    localStorage.setItem('canasta_room_code', response.room_code)

    // Emit game-created event to parent
    const gameData = {
      roomCode: response.room_code,
      playerId: response.player_id,
      gameMode: gameMode.value,
    }
    console.log('[GameLobby] Emitting game-created event:', gameData)
    emit('game-created', gameData)

    // For friends mode, show waiting room instead of starting immediately
    if (gameMode.value === 'humans') {
      console.log('[GameLobby] Humans mode - showing WaitingRoom')
      // Set current player ID for host identification
      currentPlayerId.value = response.player_id
      // Initialize waiting room with creator as first player
      waitingRoomData.value = {
        roomCode: response.room_code,
        players: [{
          id: response.player_id,
          username: props.username,
          display_name: displayName.value.trim(),
          profile_picture: profilePicture.value,
          seat: 0,
          team: 'A',
          is_bot: false,
        }],
        teams: {
          A: { name: teamConfig.value.name || 'Team A' },
          B: { name: 'Team B' },
        },
        creatorUserId: response.player_id,
        status: 'waiting',
      }
      showWaitingRoom.value = true
      mode.value = null  // Close create game screen when showing waiting room
    } else {
      // Bot mode - game starts immediately
      console.log('[GameLobby] Bots mode - transitioning to GameBoard')
      emit('game-start')
    }
  } catch (error: any) {
    console.error('[GameLobby] Failed to create game:', error)
    emit('error', error.message || 'Failed to create game')
  } finally {
    isCreating.value = false
  }
}

/**
 * Handle join game button click
 */
async function handleJoinGame() {
  if (!roomCode.value.trim() || !displayName.value.trim() || isJoining.value) return

  // Check socket connection
  if (!socket.isConnected()) {
    emit('error', 'Not connected to game server. Please refresh the page.')
    return
  }

  // Save display name to localStorage
  localStorage.setItem('canasta_display_name', displayName.value.trim())

  // Show join status modal
  isJoining.value = true
  showJoinStatus.value = true
  joinStep.value = 0
  joinError.value = false
  joinStatusMessage.value = ''

  // Listen for approval status updates
  const cleanupApprovalListener = socket.on('awaiting_join_approval', () => {
    Logger.info(LogCategory.UI, '[JOIN-STATUS] Received awaiting_join_approval, updating to step 1')
    joinStep.value = 1
    joinStatusMessage.value = 'Your request has been sent. Waiting for the room creator to approve...'
  })

  try {
    // Join room via socket - Promise resolves with seat_assigned/room_created data
    const data = await socket.joinRoom({
      roomCode: roomCode.value.trim().toUpperCase(),
      username: props.username,
      userId: props.userId,
      displayName: displayName.value.trim(),
      profilePicture: profilePicture.value,
    })

    // Clean up the approval listener
    cleanupApprovalListener()

    // joinRoom resolves when seat_assigned or room_created is received
    // The resolved data IS the success - no need to wait for separate room_joined event
    if (data) {
      Logger.info(LogCategory.UI, '[JOIN-4] Promise resolved, received data:', data)
      joinStep.value = 2
      joinStatusMessage.value = 'Approved! Loading game...'

      // Brief delay to show "Joining Game" step before transitioning
      await new Promise(resolve => setTimeout(resolve, 500))

      Logger.info(LogCategory.UI, '[JOIN-5] Emitting game-joined event')
      emit('game-joined', data)
      Logger.info(LogCategory.UI, '[JOIN-6] Emitting game-start event')
      emit('game-start')

      // Close modal
      showJoinStatus.value = false
      isJoining.value = false
    } else {
      Logger.error(LogCategory.UI, '[JOIN-ERR] No data received from joinRoom')
      joinError.value = true
      joinStatusMessage.value = 'Failed to join game - no data received'
    }
  } catch (error: any) {
    // Clean up the approval listener
    cleanupApprovalListener()

    Logger.error(LogCategory.UI, '[JOIN-ERR] Join failed:', error)
    joinError.value = true
    joinStatusMessage.value = error.message || 'Failed to join game'
  }
}

/**
 * Handle cancel button on join status modal
 */
function handleJoinCancel() {
  Logger.info(LogCategory.UI, '[JOIN-CANCEL] User cancelled join')
  showJoinStatus.value = false
  isJoining.value = false
  joinStep.value = 0
  joinError.value = false
  joinStatusMessage.value = ''
  // Note: We should also tell the server to cancel, but for now just close the modal
}

/**
 * Handle stale game recovery action from modal
 */
function handleRecoveryAction(data: { action: 'wait' | 'replace_with_bots' | 'continue' }) {
  console.log('[GameLobby] Recovery action:', data.action)

  if (data.action === 'continue') {
    // No missing players - just close modal and start game
    showRecoveryModal.value = false
    if (recoveryData.value) {
      emit('game-joined', { roomCode: recoveryData.value.room_code })
      emit('game-start')
    }
    return
  }

  // Send action to server
  socket.staleGameRecoveryAction(data.action)
}

/**
 * Resume a game from the Recent Games list
 * Handles both paused games (shows PausedGameLobby) and in-progress games (reconnects directly)
 */
async function handleResumeGame(game: GameSummary) {
  console.log('[GameLobby] Resuming game:', game.room_code, 'status:', game.status)

  // Prevent duplicate resume attempts
  if (isJoining.value) {
    console.log('[GameLobby] Already joining/resuming a game, ignoring duplicate request')
    return
  }

  // Check socket connection
  if (!socket.isConnected()) {
    emit('error', 'Not connected to game server. Please refresh the page.')
    return
  }

  isJoining.value = true

  try {
    // If game is paused, use the special paused game flow
    if (game.status === 'paused') {
      console.log('[GameLobby] Game is paused, using resumePausedGame flow')
      const result = await socket.resumePausedGame(game.id, game.room_code)
      console.log('[GameLobby] Resume paused game result:', result)
      // The paused_game_lobby event will be handled by the socket listener
      // and will show the PausedGameLobby component
    } else {
      // Use reconnectRoom for in-progress games (passes reconnect: true)
      const result = await socket.reconnectRoom({
        roomCode: game.room_code,
        username: props.username,
        userId: props.userId || undefined,
        profilePicture: profilePicture.value,
      })

      console.log('[GameLobby] Reconnected to game:', result)
      emit('game-joined', result)
      emit('game-start')
    }
  } catch (error: any) {
    console.error('[GameLobby] Failed to resume game:', error)
    emit('error', error.message || 'Failed to resume game')
  } finally {
    isJoining.value = false
  }
}

/**
 * Delete a single game from the Recent Games list
 */
async function handleDeleteGame(game: GameSummary) {
  console.log('[GameLobby] Deleting game:', game.room_code)
  const success = await gameHistory.deleteRecentGame(game.id)
  if (!success) {
    emit('error', 'Failed to delete game')
  }
}

/**
 * Delete all games from the Recent Games list
 */
async function handleDeleteAllGames() {
  console.log('[GameLobby] Deleting all recent games')
  const success = await gameHistory.deleteAllRecentGames()
  if (!success) {
    emit('error', 'Failed to delete games')
  }
}

/**
 * View game details (historical game or resumable game info)
 */
function handleViewGame(game: GameSummary) {
  console.log('[GameLobby] Viewing game details:', game.id)
  fetchGameDetail(game.id)
}

// ============================================================================
// Waiting Room Event Handlers
// ============================================================================

/**
 * Handle start game from WaitingRoom
 */
function handleWaitingRoomStart() {
  console.log('[GameLobby] Starting game from WaitingRoom')
  socket.startGame()
  showWaitingRoom.value = false
  emit('game-start')
}

/**
 * Handle add bot from WaitingRoom
 */
function handleWaitingRoomAddBot() {
  console.log('[GameLobby] Adding bot from WaitingRoom')
  socket.addBot()
}

/**
 * Handle kick player from WaitingRoom
 */
function handleWaitingRoomKick(playerId: string) {
  console.log('[GameLobby] Kicking player from WaitingRoom:', playerId)
  socket.kickPlayer(playerId)
}

/**
 * Handle leave game from WaitingRoom
 */
function handleWaitingRoomLeave() {
  console.log('[GameLobby] Leaving game from WaitingRoom')
  socket.leaveRoom(waitingRoomData.value.roomCode)
  showWaitingRoom.value = false
  currentPlayerId.value = ''
  waitingRoomData.value = {
    roomCode: '',
    players: [],
    teams: { A: { name: 'Team A' }, B: { name: 'Team B' } },
    creatorUserId: '',
    status: 'waiting',
  }
}

/**
 * Handle back button from WaitingRoom
 */
function handleWaitingRoomBack() {
  console.log('[GameLobby] Going back from WaitingRoom')
  socket.leaveRoom(waitingRoomData.value.roomCode)
  showWaitingRoom.value = false
  currentPlayerId.value = ''
  waitingRoomData.value = {
    roomCode: '',
    players: [],
    teams: { A: { name: 'Team A' }, B: { name: 'Team B' } },
    creatorUserId: '',
    status: 'waiting',
  }
}

// ============================================================================
// Paused Game Lobby Event Handlers
// ============================================================================

/**
 * Handle back button from PausedGameLobby
 */
function handlePausedLobbyBack() {
  console.log('[GameLobby] Going back from PausedGameLobby')
  socket.leaveRoom(pausedGameData.value.roomCode)
  showPausedGameLobby.value = false
  pausedGameData.value = {
    roomCode: '',
    gameId: '',
    players: [],
    roundNumber: 1,
    teamAName: 'Team A',
    teamBName: 'Team B',
    teamAScore: 0,
    teamBScore: 0,
    creatorUserId: '',
  }
}

/**
 * Handle resume game from PausedGameLobby
 */
function handlePausedLobbyResume() {
  console.log('[GameLobby] Resuming game from PausedGameLobby')
  socket.startPausedGame()
  // The game_started event will handle hiding the lobby and emitting game-start
}

/**
 * Handle replace with bot from PausedGameLobby
 */
function handlePausedLobbyReplaceBot(seatPosition: number) {
  console.log('[GameLobby] Replacing with bot in PausedGameLobby, seat:', seatPosition)
  socket.replaceWithBotPaused(seatPosition)
  // The paused_lobby_updated event will update the player list
}

/**
 * Handle send invite from PausedGameLobby
 */
function handlePausedLobbySendInvite(seatPosition: number) {
  console.log('[GameLobby] Sending invite from PausedGameLobby for seat:', seatPosition)
  // The PausedPlayerSlot already copies the room code
  // Here we could integrate with a share API or notification system in the future
}

/**
 * Format game status for display (kept for future use)
 */
function _formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    waiting: 'Waiting',
    in_progress: 'In Progress',
    completed: 'Completed',
  }
  return statusMap[status] || status
}
void _formatStatus // Suppress unused variable warning

/**
 * Format timestamp to relative time (kept for future use)
 */
function _formatTime(timestamp: string): string {
  const now = new Date()
  const then = new Date(timestamp)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return then.toLocaleDateString()
}
void _formatTime // Suppress unused variable warning

/**
 * Handle logout from LobbyMenu
 */
function handleLogout() {
  emit('logout')
}

/**
 * Handle How to Play from LobbyMenu
 */
function handleHowToPlay() {
  showHowToPlay.value = true
}

/**
 * Handle Interface Guide from LobbyMenu
 */
function handleInterfaceGuide() {
  tutorial.startTutorial('lobby')
}

/**
 * Handle Profile from LobbyMenu
 */
function handleProfile() {
  emit('profile')
}

/**
 * Handle starting tutorial from first-visit prompt
 */
function handleStartTutorial() {
  showFirstVisitPrompt.value = false
  if (neverShowAgain.value) {
    tutorial.setAutoPromptDisabled(true)
  }
  // Start the guided first-time flow
  tutorial.startFirstTimeFlow()
}

/**
 * Handle skipping tutorial from first-visit prompt
 */
function handleSkipFirstVisit() {
  showFirstVisitPrompt.value = false
  tutorial.markFirstVisitSeen()
  if (neverShowAgain.value) {
    tutorial.setAutoPromptDisabled(true)
  }
}

/**
 * Check for first-time user and show welcome prompt
 */
function checkFirstTimeUser() {
  if (tutorial.isFirstTimeUser.value) {
    // Small delay to let the page render first
    setTimeout(() => {
      showFirstVisitPrompt.value = true
    }, 500)
  }
}
</script>

<style scoped>
/* ============================================================================
   Lobby Container
   ============================================================================ */

.lobby-container {
  min-height: 100vh;
  background: #0d1f2d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Subtle animated background effect */
.lobby-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(232, 197, 71, 0.03) 0%, transparent 70%);
  animation: pulse 8s ease-in-out infinite;
  pointer-events: none;
}

@keyframes pulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
}

/* Responsive: Scale content down on shorter viewports */
@media (max-height: 800px) {
  .welcome-section {
    margin-bottom: 1.5rem;
  }

  .mode-cards {
    gap: 1rem;
  }

  .mode-card {
    padding: 2rem 1.5rem;
  }

  .mode-icon {
    font-size: 3.5rem;
    margin-bottom: 1rem;
  }

  .active-games-section {
    margin-top: 2rem;
  }
}

/* ============================================================================
   Mode Selection Screen
   ============================================================================ */

.mode-selection {
  position: relative;
  z-index: 10;
  width: 100%;
  height: calc(100vh - 2rem);
  display: flex;
  padding: 1rem 3rem;
}

/* ============================================================================
   Lobby Menu Positioning
   ============================================================================ */

.lobby-menu-wrapper {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 10000;
}

/* ============================================================================
   Logo Column - Fixed positioning like the menu (opposite side)
   ============================================================================ */

.logo-column {
  position: fixed;
  top: 1rem;
  left: 1.5rem;
  z-index: 100;
}

.lobby-logo {
  max-width: 150px;
  width: 100%;
  height: auto;
  filter: drop-shadow(0 4px 12px rgba(232, 197, 71, 0.3));
  animation: logoFloat 3s ease-in-out infinite;
}

@keyframes logoFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* ============================================================================
   Main Content Area
   ============================================================================ */

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
  max-width: 800px;
  overflow: hidden;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.welcome-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.welcome-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #e8c547;
  margin: 0;
  text-shadow: 0 2px 12px rgba(232, 197, 71, 0.3);
  letter-spacing: -0.02em;
}

.subtitle {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  margin: 0;
}

/* .history-btn replaced with .u-glass-btn utility class */

/* ============================================================================
   Content Body (Vertical Layout - Actions Row + Games Section Below)
   ============================================================================ */

.content-body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;
  min-height: 0;
}

.actions-row {
  display: flex;
  gap: 1rem;
  flex-shrink: 0;
}

.actions-row .mode-card {
  flex: 1;
}

.games-section {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.recent-games-section,
.history-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.section-title .material-symbols-outlined {
  font-size: 1.125rem;
  color: #e8c547;
}

.game-count {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

/* Legacy welcome section - kept for loading/error states */
.welcome-section {
  text-align: center;
  margin-bottom: clamp(1.5rem, 3vh, 3rem);
  animation: floatIn 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.error-text {
  color: #ef4444;
  margin-bottom: 1.5rem;
}

/* .retry-button replaced with .u-btn-primary utility class */

/* ============================================================================
   Lobby Tabs
   ============================================================================ */

.lobby-tabs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  animation: fadeIn 0.4s ease-out;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.tab-button .material-symbols-outlined {
  font-size: 1.25rem;
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(232, 197, 71, 0.3);
}

.tab-button.active {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(232, 197, 71, 0.1));
  border-color: rgba(232, 197, 71, 0.5);
  color: #e8c547;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  background: #4ade80;
  border-radius: 9999px;
  color: #0a0a0a;
  font-size: 0.7rem;
  font-weight: 700;
}

.tab-content {
  min-height: 300px;
  animation: fadeIn 0.3s ease-out;
}

.actions-tab,
.recent-tab,
.history-tab {
  max-width: 800px;
  margin: 0 auto;
}

/* ============================================================================
   Mode Cards (Actions Column)
   ============================================================================ */

.mode-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.actions-row .mode-card {
  position: relative;
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.95) 0%, rgba(26, 47, 63, 0.95) 100%);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.25rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  overflow: hidden;
}

/* Legacy mode-card styles for backwards compatibility */
.mode-card {
  position: relative;
  flex: 0 0 auto;
  width: clamp(250px, 25vw, 300px);
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.95) 0%, rgba(26, 47, 63, 0.95) 100%);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: clamp(2rem, 3vh, 3rem) clamp(1.5rem, 2vw, 2rem);
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  overflow: hidden;
}

/* Subtle gradient overlay on cards */
.mode-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.05) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.4s;
  pointer-events: none;
}

.mode-card:hover::after {
  opacity: 1;
}

.mode-card:hover {
  transform: translateY(-12px) scale(1.02);
  border-color: rgba(232, 197, 71, 0.4);
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(232, 197, 71, 0.3),
    0 16px 48px rgba(232, 197, 71, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.mode-card:active {
  transform: translateY(-8px) scale(1);
  transition: all 0.15s;
}

/* Mode icon and text for actions row */
.actions-row .mode-icon {
  font-size: 2.5rem;
  color: #e8c547;
  margin-bottom: 0.75rem;
  filter: drop-shadow(0 2px 8px rgba(232, 197, 71, 0.4));
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.actions-row .mode-card:hover .mode-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 4px 12px rgba(232, 197, 71, 0.5));
}

.actions-row .mode-card h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.375rem;
  position: relative;
  z-index: 1;
  transition: color 0.25s;
}

.actions-row .mode-card:hover h3 {
  color: #e8c547;
}

.actions-row .mode-card p {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  position: relative;
  z-index: 1;
  margin: 0;
}

/* Legacy mode-icon styles */
.mode-icon {
  font-size: clamp(3.5rem, 5vh, 5rem);
  color: #e8c547;
  margin-bottom: clamp(1rem, 2vh, 1.5rem);
  filter: drop-shadow(0 4px 16px rgba(232, 197, 71, 0.4));
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

.mode-card:hover .mode-icon {
  transform: scale(1.1) translateY(-4px);
  filter: drop-shadow(0 8px 24px rgba(232, 197, 71, 0.6));
}

.mode-card h3 {
  font-size: clamp(1.25rem, 2.5vh, 1.625rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: clamp(0.5rem, 1vh, 0.75rem);
  position: relative;
  z-index: 1;
  transition: color 0.3s;
}

.mode-card:hover h3 {
  color: #e8c547;
}

.mode-card p {
  font-size: clamp(0.875rem, 1.5vh, 1rem);
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.5;
  position: relative;
  z-index: 1;
  transition: color 0.3s;
}

.mode-card:hover p {
  color: rgba(255, 255, 255, 0.85);
}

/* ============================================================================
   Recent Games Inline Section (on Play tab)
   ============================================================================ */

.recent-games-inline {
  max-width: 1000px;
  margin: clamp(2rem, 4vh, 3rem) auto 0;
  animation: floatIn 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both;
}

.recent-games-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: clamp(1rem, 2vh, 1.25rem);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  margin-bottom: clamp(1rem, 2vh, 1.5rem);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.recent-games-title .material-symbols-outlined {
  font-size: 1.25em;
  color: var(--primary-gold, #e8c547);
}

/* ============================================================================
   Active Games Section
   ============================================================================ */

.active-games-section {
  max-width: 1000px;
  margin: clamp(2rem, 4vh, 4rem) auto 0;
  animation: floatIn 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both;
}

.active-games-title {
  font-size: clamp(1.125rem, 2.5vh, 1.5rem);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-bottom: clamp(1rem, 2vh, 2rem);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Empty state */
.no-games {
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.6) 0%, rgba(26, 47, 63, 0.6) 100%);
  border: 2px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  backdrop-filter: blur(8px);
}

.no-games-icon {
  font-size: 4rem;
  color: rgba(232, 197, 71, 0.4);
  margin-bottom: 1rem;
}

.no-games-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.5rem;
}

.no-games-hint {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

/* Active games grid */
.active-games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

/* Individual game card */
.game-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.9) 0%, rgba(26, 47, 63, 0.9) 100%);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  overflow: hidden;
}

.game-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.05) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.game-card:hover::before {
  opacity: 1;
}

.game-card:hover {
  transform: translateY(-6px) scale(1.02);
  border-color: rgba(232, 197, 71, 0.3);
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(232, 197, 71, 0.2),
    0 8px 24px rgba(232, 197, 71, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.game-card:active {
  transform: translateY(-4px) scale(1);
  transition: all 0.15s;
}

/* Game card header */
.game-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.game-room-code {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e8c547;
  text-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
  letter-spacing: 0.1em;
}

.game-status {
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.game-status.waiting {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.game-status.in_progress {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.game-status.completed {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
  border: 1px solid rgba(156, 163, 175, 0.3);
}

/* Game card body */
.game-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.game-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
}

.game-icon {
  font-size: 1.25rem;
  color: rgba(232, 197, 71, 0.6);
}

.game-players,
.game-time {
  font-weight: 500;
}

/* Game card footer */
.game-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.rejoin-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(232, 197, 71, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.game-card-footer .material-symbols-outlined {
  font-size: 1.25rem;
  color: rgba(232, 197, 71, 0.6);
  transition: transform 0.3s;
}

.game-card:hover .game-card-footer .material-symbols-outlined {
  transform: translateX(4px);
  color: #e8c547;
}

/* ============================================================================
   Create/Join Game Screens
   ============================================================================ */

.create-game-screen,
.join-game-screen {
  position: relative;
  max-width: 650px;
  width: 100%;
  animation: fadeIn 0.4s ease-out;
  z-index: 10;
  padding: 0 1rem;
  background: transparent;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

/* Glassmorphism Container */
.glass-container {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 28px;
  padding: 2rem 1.75rem;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
}

/* Subtle glow effect */
.glass-container::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 28px;
  padding: 2px;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(232, 197, 71, 0.05), rgba(232, 197, 71, 0.2));
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.5;
  pointer-events: none;
}

.screen-header {
  text-align: center;
  margin-bottom: 1.5rem;
  position: relative;
}

.screen-title {
  font-size: clamp(1.5rem, 3.5vw, 1.875rem);
  font-weight: 700;
  color: #e8c547;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 12px rgba(232, 197, 71, 0.3);
}

/* ============================================================================
   Form Sections
   ============================================================================ */

.form-section {
  margin-bottom: 1.25rem;
}

.section-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.5rem;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.input-hint {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.375rem;
  margin-bottom: 0;
}

.select-icon-label {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
}

/* Avatar Carousel - Wheel Effect */
.avatar-carousel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  perspective: 800px;
  padding: 1rem 0;
}

.avatar-carousel.spinning .carousel-item {
  transition: all 0.08s linear;
}

.carousel-track {
  position: relative;
  width: 200px;
  height: 120px;
  transform-style: preserve-3d;
}

.carousel-item {
  position: absolute;
  border-radius: 50%;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 3px solid rgba(255, 255, 255, 0.15);
  background: rgba(20, 25, 35, 0.9);
}

.carousel-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.carousel-item.current {
  width: 100px;
  height: 100px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) translateZ(0) rotateY(0deg);
  z-index: 3;
  border-color: #e8c547;
  box-shadow: 0 0 30px rgba(232, 197, 71, 0.5), 0 8px 32px rgba(0, 0, 0, 0.3);
}

.carousel-item.prev {
  width: 65px;
  height: 65px;
  left: 10px;
  top: 50%;
  transform: translateY(-50%) translateZ(-40px) rotateY(35deg);
  opacity: 0.5;
  z-index: 2;
  filter: brightness(0.6);
  border-color: rgba(255, 255, 255, 0.1);
}

.carousel-item.next {
  width: 65px;
  height: 65px;
  right: 10px;
  top: 50%;
  transform: translateY(-50%) translateZ(-40px) rotateY(-35deg);
  opacity: 0.5;
  z-index: 2;
  filter: brightness(0.6);
  border-color: rgba(255, 255, 255, 0.1);
}

.carousel-arrow {
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(232, 197, 71, 0.3);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  flex-shrink: 0;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.8);
}

.carousel-arrow:hover:not(:disabled) {
  background: rgba(232, 197, 71, 0.2);
  border-color: #e8c547;
  color: #e8c547;
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(232, 197, 71, 0.3);
}

.carousel-arrow:active:not(:disabled) {
  transform: scale(0.95);
}

.carousel-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.carousel-arrow .material-symbols-outlined {
  font-size: 1.5rem;
}

/* Random Avatar Button */
.random-avatar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -38px auto -20px;
  background: transparent;
  border: none;
  font-size: 3rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.random-avatar-btn:hover:not(:disabled) {
  transform: scale(1.2) rotate(15deg);
  filter: drop-shadow(0 4px 8px rgba(232, 197, 71, 0.4));
}

.random-avatar-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.random-avatar-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* 3D Dice Icon */
.dice-3d {
  width: 24px;
  height: 24px;
  position: relative;
  transform-style: preserve-3d;
  transform: rotateX(-20deg) rotateY(30deg);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.dice-3d::before,
.dice-3d::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 3px;
}

/* Top face - showing 1 */
.dice-3d::before {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  transform: rotateX(90deg) translateZ(12px);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1);
}

.dice-3d::before {
  background:
    radial-gradient(circle at 50% 50%, #1a1a2e 3px, transparent 3px),
    linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
}

/* Front face - showing 5 */
.dice-3d::after {
  background:
    radial-gradient(circle at 25% 25%, #1a1a2e 2.5px, transparent 2.5px),
    radial-gradient(circle at 75% 25%, #1a1a2e 2.5px, transparent 2.5px),
    radial-gradient(circle at 50% 50%, #1a1a2e 2.5px, transparent 2.5px),
    radial-gradient(circle at 25% 75%, #1a1a2e 2.5px, transparent 2.5px),
    radial-gradient(circle at 75% 75%, #1a1a2e 2.5px, transparent 2.5px),
    linear-gradient(135deg, #ffffff 0%, #e8e8e8 100%);
  transform: translateZ(12px);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.08);
}

/* Right face using box element */
.dice-3d-face {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 3px;
  background:
    radial-gradient(circle at 25% 50%, #1a1a2e 2.5px, transparent 2.5px),
    radial-gradient(circle at 75% 50%, #1a1a2e 2.5px, transparent 2.5px),
    linear-gradient(135deg, #d8d8d8 0%, #c0c0c0 100%);
  transform: rotateY(90deg) translateZ(12px);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.12);
}

/* Team Name Input with Randomize Button */
.team-name-input {
  display: flex !important;
  flex-direction: row !important;
  gap: 0.5rem;
  width: 100% !important;
  max-width: none !important;
  align-items: stretch;
}

.team-name-input .text-input {
  flex: 1 1 0% !important;
  min-width: 0 !important;
  max-width: none !important;
  width: auto !important;
}

.team-name-field.generated-name {
  color: rgba(255, 255, 255, 0.5);
}

.team-name-field.generated-name::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.randomize-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  font-size: 2.5rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  padding: 0.5rem;
}

.randomize-btn:hover {
  transform: scale(1.2) rotate(15deg);
  filter: drop-shadow(0 4px 8px rgba(232, 197, 71, 0.4));
}

.randomize-btn:active {
  transform: scale(0.95);
}

/* Icon Grid */
.icon-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.icon-btn {
  width: 50px !important;
  height: 50px !important;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(8px);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.icon-btn .material-symbols-outlined {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
}

.icon-btn:hover {
  border-color: rgba(232, 197, 71, 0.6);
  background: rgba(232, 197, 71, 0.08);
  transform: scale(1.08);
  box-shadow: 0 4px 16px rgba(232, 197, 71, 0.25);
}

.icon-btn.selected {
  border-color: #e8c547;
  border-width: 2.5px;
  background: rgba(232, 197, 71, 0.12);
  box-shadow: 0 0 20px rgba(232, 197, 71, 0.5);
  transform: scale(1.05);
}

.icon-btn.selected .material-symbols-outlined {
  color: #e8c547;
}

/* Game Mode Cards */
.game-mode-cards {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  grid-auto-rows: minmax(85px, auto);
  width: 100%; /* Ensure grid doesn't exceed container width */
  box-sizing: border-box;
}

.mode-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border: 2px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(8px);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  /* Label element resets - fix browser default styling */
  line-height: 1;
  margin: 0;
  box-sizing: border-box;
  vertical-align: top;
  display: block; /* Force label to behave as block element, not inline */

  /* Grid cell sizing - constrain to grid cell dimensions */
  width: 100%; /* Constrain to grid column width */
  min-height: 90px; /* Minimum height for content */
  max-width: 100%; /* Prevent overflow beyond grid cell */

  /* Restore flexbox for layout */
  display: flex;
}

.mode-card:hover {
  border-color: rgba(232, 197, 71, 0.5);
  background: rgba(232, 197, 71, 0.06);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(232, 197, 71, 0.2);
}

.mode-card.active {
  border-color: #e8c547;
  background: rgba(232, 197, 71, 0.1);
  box-shadow: 0 0 24px rgba(232, 197, 71, 0.3);
}

.mode-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.mode-indicator {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  position: relative;
  transition: all 0.25s;
  align-self: center; /* Keep indicator centered even when text wraps */
}

.mode-card.active .mode-indicator {
  border-color: #e8c547;
  background: #e8c547;
  box-shadow: 0 0 12px rgba(232, 197, 71, 0.5);
}

.mode-card.active .mode-indicator::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0d1f2d;
}

.mode-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0; /* Allow flexbox child to shrink below content size */
}

.mode-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.2;
  white-space: normal; /* Ensure text can wrap */
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.mode-desc {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.3;
}

/* Join Game Screen - Vertical Layout */
.join-game-screen .form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.join-game-screen .form-section {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.join-game-screen .section-label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.375rem;
}

/* Text Inputs */
.text-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.15);
}

.text-input:hover {
  border-color: rgba(232, 197, 71, 0.3);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.1);
}

.text-input:focus {
  outline: none;
  border-color: #e8c547;
  background: rgba(255, 255, 255, 0.1);
  box-shadow:
    0 0 0 3px rgba(232, 197, 71, 0.2),
    inset 0 2px 6px rgba(0, 0, 0, 0.1),
    0 8px 24px rgba(232, 197, 71, 0.15);
  transform: translateY(-2px);
}

.text-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.room-code-input {
  text-transform: uppercase;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.25rem;
  text-align: center;
}

.input-hint {
  font-size: 0.625rem;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

/* Game Mode Radio Options */
.game-mode-section {
  gap: 0.375rem;
}

.radio-option {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  border: 2px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.radio-option:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(232, 197, 71, 0.3);
}

.radio-option.active {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.18) 0%, rgba(232, 197, 71, 0.08) 100%);
  border-color: #e8c547;
  box-shadow: 0 4px 16px rgba(232, 197, 71, 0.2);
}

.radio-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.radio-circle {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  position: relative;
  transition: all 0.2s ease;
  margin-top: 0.125rem;
}

.radio-option.active .radio-circle {
  border-color: #e8c547;
  background: #e8c547;
  box-shadow: 0 0 12px rgba(232, 197, 71, 0.5);
}

.radio-option.active .radio-circle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #0d1f2d;
}

.radio-content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
}

.radio-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
}

.radio-desc {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.3;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.btn-create {
  flex: 1;
  position: relative;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #e8c547 0%, #d4b13a 100%);
  color: #0d1f2d;
  font-size: 0.9375rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 6px 20px rgba(232, 197, 71, 0.4),
    0 2px 8px rgba(232, 197, 71, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  overflow: hidden;
}

/* Shine effect on button */
.btn-create::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s;
}

.btn-create:hover:not(:disabled)::before {
  left: 100%;
}

.btn-create:hover:not(:disabled) {
  background: linear-gradient(135deg, #ffd95f 0%, #e8c547 100%);
  transform: translateY(-3px) scale(1.02);
  box-shadow:
    0 12px 32px rgba(232, 197, 71, 0.5),
    0 6px 16px rgba(232, 197, 71, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.btn-create:active:not(:disabled) {
  transform: translateY(-1px) scale(1);
  transition: all 0.15s;
}

.btn-create:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.3) 0%, rgba(212, 177, 58, 0.3) 100%);
  box-shadow: none;
}

.btn-cancel {
  padding: 0.875rem 1.5rem;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9375rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
  transform: translateY(-2px);
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.btn-cancel:active {
  transform: translateY(0);
  transition: all 0.15s;
}

/* Responsive Design */
@media (max-width: 640px) {
  .create-game-screen,
  .join-game-screen {
    padding: 0 0.75rem;
  }

  .screen-title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-section {
    margin-bottom: 1.5rem;
  }

  .section-label {
    font-size: 1rem;
  }

  .avatar-carousel {
    gap: 0.5rem;
  }

  .carousel-arrow {
    width: 40px;
    height: 40px;
  }

  .carousel-item.current {
    width: 80px;
    height: 80px;
  }

  .carousel-item.prev,
  .carousel-item.next {
    width: 48px;
    height: 48px;
  }

  .game-mode-cards {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-buttons .btn-create,
  .action-buttons .btn-cancel {
    width: 100%;
  }

  .btn-cancel {
    order: -1;
  }

  /* Active games responsive */
  .active-games-section {
    margin-top: 3rem;
  }

  .active-games-title {
    font-size: 1.25rem;
  }

  .active-games-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .mode-cards {
    flex-direction: column;
    gap: 1.5rem;
  }

  .mode-card {
    width: 100%;
    max-width: 400px;
  }
}

/* ============================================================================
   Responsive Breakpoints for New Layout
   ============================================================================ */

/* Tablet (< 1024px) - Reduce logo, adjust content */
@media (max-width: 1024px) {
  .mode-selection {
    padding: 0.75rem;
  }

  .logo-column {
    left: 1rem;
  }

  .lobby-logo {
    max-width: 120px;
  }

  .actions-row .mode-card {
    max-width: 220px;
  }

  .content-body {
    gap: 1rem;
  }
}

/* Mobile (< 768px) - Move logo to top, stack vertically */
@media (max-width: 768px) {
  .mode-selection {
    flex-direction: column;
    height: auto;
    min-height: calc(100vh - 2rem);
    padding: 80px 0.5rem 0.5rem 0.5rem; /* Top padding for logo */
  }

  .logo-column {
    position: fixed;
    top: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
  }

  .lobby-logo {
    max-width: 70px;
  }

  .main-content {
    gap: 1rem;
  }

  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .history-btn {
    align-self: flex-end;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
  }

  .content-body {
    gap: 1rem;
    overflow-y: auto;
  }

  .actions-row {
    flex-shrink: 0;
    gap: 0.75rem;
  }

  .actions-row .mode-card {
    flex: 1;
    max-width: none;
    padding: 1rem 0.75rem;
  }

  .actions-row .mode-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .actions-row .mode-card h3 {
    font-size: 0.9375rem;
  }

  .actions-row .mode-card p {
    font-size: 0.75rem;
  }

  .games-section {
    flex: 1;
    min-height: 250px;
  }

  .section-title {
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
  }
}

/* Small mobile (< 480px) - Further compaction */
@media (max-width: 480px) {
  .actions-row {
    flex-direction: column;
  }

  .actions-row .mode-card {
    flex: 0 0 auto;
    width: 100%;
  }

  .welcome-title {
    font-size: 1.25rem;
  }
}

/* ============================================================================
   First-Visit Welcome Modal
   ============================================================================ */

.first-visit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 1rem;
}

.first-visit-modal {
  background: linear-gradient(
    135deg,
    rgba(13, 31, 45, 0.98) 0%,
    rgba(26, 47, 63, 0.98) 100%
  );
  border: 1px solid rgba(232, 197, 71, 0.3);
  border-radius: 20px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.6),
    0 0 32px rgba(232, 197, 71, 0.15);
  max-width: 420px;
  width: 100%;
  text-align: center;
  overflow: hidden;
}

.first-visit-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.first-visit-header .welcome-icon {
  font-size: 48px;
  color: #e8c547;
  margin-bottom: 12px;
  display: block;
}

.first-visit-header h2 {
  margin: 0;
  font-family: 'Cinzel', 'Playfair Display', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #f5f0e1;
}

.first-visit-body {
  padding: 20px 24px;
}

.first-visit-body p {
  margin: 0 0 8px;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.first-visit-body .hint-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0;
}

.first-visit-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* Button styles replaced with .u-glass-btn and .u-btn-primary utility classes */
.first-visit-footer button {
  flex: 1;
}

.never-show-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px 20px;
  cursor: pointer;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.2s ease;
}

.never-show-checkbox:hover {
  color: rgba(255, 255, 255, 0.7);
}

.never-show-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #e8c547;
  cursor: pointer;
}

/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .first-visit-modal,
.modal-fade-leave-to .first-visit-modal {
  transform: scale(0.95);
}
</style>
